import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import SectionCard from '@/components/ui/SectionCard'
import Skeleton from '@/components/ui/Skeleton'
import StatStrip, { type StatStripItem } from '@/components/ui/StatStrip'
import StatStripSkeleton from '@/components/ui/StatStripSkeleton'
import TitleRow from '@/components/ui/TitleRow'
import {
  sectorBrokersQueryOptions,
  useSectorBrokers,
  useSectorFlow,
} from '@/features/brokers/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { formatQuantity } from '@/lib/format'
import { sentenceCase } from '@/lib/text'
import { resolveViewState } from '@/lib/view-state'
import { cn } from '@/lib/utils'
import type { SectorFlow } from '@/schemas/broker'
import { loadSectorFlow } from '@/screens/brokers/SectorFlowScreen.loader'
import { sectorFlowSearchSchema, type SectorFlowSearch } from '@/screens/brokers/search'
import WindowNote from '@/screens/brokers/components/broker/WindowNote'
import BrokerBySectorCard from '@/screens/brokers/components/sector-flow/BrokerBySectorCard'
import BrokerBySectorSkeleton from '@/screens/brokers/components/sector-flow/BrokerBySectorSkeleton'
import SectorBrokersTable from '@/screens/brokers/components/sector-flow/SectorBrokersTable'
import SectorFlowEmpty from '@/screens/brokers/components/sector-flow/SectorFlowEmpty'
import SectorFlowTable from '@/screens/brokers/components/sector-flow/SectorFlowTable'

const HEADER = <TitleRow title="Sector Flow" />

/**
 * NEPSE's own sub-index count, the same "typical, not guaranteed" count
 * `MarketBoardStates.tsx`'s `SECTOR_TILES` uses for the same reason: both
 * read off `/market/sectors/`'s usual day, and the sector-flow table is the
 * broker slice of that same sector list.
 */
const TYPICAL_SECTORS = 13

/** SectionCard's own 36px head row, a bar where its title goes and one for its meta. */
function CardHeaderSkeleton({ title }: { title: string }) {
  return (
    <div className="flex min-h-9 items-center justify-between gap-3">
      <Skeleton className={cn('h-4 rounded-xs', title)} />
      <Skeleton className="h-3 w-24 rounded-xs" />
    </div>
  )
}

/**
 * The overview's top two blocks while loading: the three-cell panel strip and
 * the sector table's own panel, at a typical day's row count.
 */
function OverviewTopSkeleton() {
  return (
    <>
      <StatStripSkeleton cells={4} surface="panel" size="m" />
      <DataTableSkeleton rows={TYPICAL_SECTORS} columns={4} />
    </>
  )
}

/**
 * The two branches `SectorFlowScreen` itself renders (see its own doc
 * comment): with no `?sector=`, the market-wide summary + table + the
 * "Broker by sector" card; with one, the back link over just that sector's
 * table. The Broker by sector card's skeleton is its own light file, shared
 * with the card's own loading state, so the two cannot drift apart.
 */
function SectorFlowPending() {
  const { sector } = Route.useSearch()

  return (
    <ScreenLayout
      pending
      pendingLabel="Loading sector flow…"
      header={HEADER}
      pageTitle="Sector Flow"
    >
      {sector === undefined ? (
        <div className="flex flex-col gap-6">
          <OverviewTopSkeleton />
          <BrokerBySectorSkeleton />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <Skeleton className="h-4 w-24 rounded-xs" />
          <div className={SECTION_CARD}>
            <CardHeaderSkeleton title="w-24" />
            <DataTableSkeleton bare rows={1} columns={4} className="mt-3" />
          </div>
        </div>
      )}
    </ScreenLayout>
  )
}

/**
 * Kept light per docs/route-loading.md, "Error components": `RouteErrorPage`
 * plus the same `HEADER` the pending page shows, nothing else —
 * `errorComponent`s ship in the entry chunk now, so importing
 * `SectorFlowEmpty`/`Link` here (as an earlier version of this file did, for
 * `NO_FLOORSHEET_DATA`) would bloat it. That case no longer reaches here at
 * all: `sectorFlowQueryOptions` / `sectorBrokersQueryOptions` (queries.ts)
 * catch it and resolve `null` instead of throwing, so it is
 * `SectorFlowOverview` / `SectorFlowDrilldown`'s own `data === null` branch
 * — "Not-found answers" — same as a real failure would be if it
 * only happened after the page was already on screen. Only a GENUINE failure
 * on the very first load reaches this component, and it keeps the exact
 * title each branch already used (`"Couldn't load sector flow"` /
 * `"Couldn't load {sector}"`), which `SectorFlowScreen.test.tsx` pins.
 */
function SectorFlowError(props: ErrorComponentProps) {
  const { sector } = Route.useSearch()

  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title={sector === undefined ? "Couldn't load sector flow" : `Couldn't load ${sector}`}
    />
  )
}

export const Route = createFileRoute('/_app/brokers/sector-flow')({
  validateSearch: sectorFlowSearchSchema,
  loader: loadSectorFlow,
  pendingComponent: SectorFlowPending,
  errorComponent: SectorFlowError,
  component: SectorFlowScreen,
})

/** Pinned to `'today'` by the route's own schema — see `sectorFlowSearchSchema`. */
type SectorFlowPeriod = SectorFlowSearch['period']

/**
 * The mock's four cells (the pages revamp, 2026-10-28): the sector money moved into most, the one
 * it left most, Rotation strength ("38 of 100") and how many brokers bought the top sector ("61 of
 * 91"). Each value is a name or a count in the 16 rung with no line under it; the net amounts left
 * the strip (the sector rows' card carries them).
 *
 * Rotation strength has no source: no score exists anywhere in `SectorFlowSerializer`, so the
 * cell is drawn and says "Not available yet" until the wire carries `rotation`
 * (`asks/sector-flow.md`). The net-buyer count lives only on the per-sector read
 * (`/sector-flow/{sector}/`, the drill-down's own), so its cell costs one more request, for the
 * most-money-in sector: the loader starts it beside this page's own read, and a row click into
 * that sector finds it cached. The cell holds a bar while it is out and is left off if it fails
 * or the sector has not been summarised, rather than printing a dash in a figure's place. The
 * sector name is backend text with no bounded length (Issue 16), so it truncates.
 */
function SectorSummaryStrip({
  mostIn,
  mostOut,
  rotation,
  period,
}: Pick<SectorFlow, 'mostIn' | 'mostOut' | 'rotation'> & { period: SectorFlowPeriod }) {
  // Not `useSectorBrokers`: its key needs a sector, and a day with no
  // most-money-in sector has none to ask about.
  const buyers = useQuery({
    ...sectorBrokersQueryOptions(mostIn?.sector ?? '', period),
    enabled: mostIn !== null,
  })

  if (mostIn === null && mostOut === null) return null

  const items: StatStripItem[] = []
  if (mostIn !== null) {
    items.push({ label: 'Most money in', value: sentenceCase(mostIn.sector), truncate: true })
  }
  if (mostOut !== null) {
    items.push({ label: 'Most money out', value: sentenceCase(mostOut.sector), truncate: true })
  }
  items.push({
    label: 'Rotation strength',
    value:
      rotation === null || rotation === undefined ? (
        <EmptyState
          kind="not-available"
          size="compact"
          thing="Rotation strength"
          className="items-start"
        />
      ) : (
        `${formatQuantity(rotation)} of 100`
      ),
  })
  if (mostIn !== null && (buyers.isPending || (buyers.data ?? null) !== null)) {
    items.push({
      label: `Brokers buying ${sentenceCase(mostIn.sector).toLowerCase()}`,
      value:
        buyers.data === undefined || buyers.data === null ? (
          // The figure's own line, so the cell does not grow when it lands.
          <span className="flex h-6 items-center">
            <Skeleton className="h-4 w-20 rounded-xs" />
          </span>
        ) : (
          `${formatQuantity(buyers.data.netBuyers)} of ${formatQuantity(buyers.data.totalBrokers)}`
        ),
      truncate: true,
    })
  }

  return <StatStrip items={items} surface="panel" size="m" />
}

function SectorFlowOverview({ period }: { period: SectorFlowPeriod }) {
  const online = useOnline()
  const query = useSectorFlow(period)
  const data = query.data
  // `data === null` is `nullOnNoFloorsheetData`'s answer for `NO_FLOORSHEET_DATA`
  // (queries.ts) — the nightly summary has not run, not a failure, so it is
  // its own branch rather than something `resolveViewState` classifies. A
  // genuinely empty `sectors` on a 200 reads the same way: both mean "not
  // summarised yet", and `SectorFlowEmpty` already says so for either.
  const isEmpty = (d: SectorFlow | null) => d === null || d.sectors.length === 0
  // A failed background refetch keeps today's sectors on screen; only a
  // data-less query reads as failed/offline/empty (CRS-ST-2).
  const view = resolveViewState(query, { isEmpty, online })

  if (view === 'loading') {
    return (
      <div className="flex flex-col gap-6">
        <OverviewTopSkeleton />
      </div>
    )
  }

  if (view === 'empty') return <SectorFlowEmpty />

  if (view === 'failed' || view === 'offline') {
    return (
      <EmptyState
        kind={view}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
        savedAt={query.dataUpdatedAt}
        {...(view === 'failed' ? { title: "Couldn't load sector flow" } : {})}
      />
    )
  }

  if (data === undefined || data === null) return null

  return (
    <div className="flex flex-col gap-6">
      <SectorSummaryStrip
        mostIn={data.mostIn}
        mostOut={data.mostOut}
        rotation={data.rotation}
        period={period}
      />

      {/* The table is its own panel with no head, as the revamp draws it: the
          section strip names the page and the strip above says "today". The
          window's "As of {date}" line is Broker by sector's, the same nightly
          summary as this table (`services/broker/sectorflow.py` hard-codes
          `source="precomputed"`, so there is no partial window to warn of). */}
      <SectorFlowTable period={period} rows={data.sectors} isLoading={false} />

      <BrokerBySectorCard period={period} />
    </div>
  )
}

function SectorFlowDrilldown({ sector, period }: { sector: string; period: SectorFlowPeriod }) {
  const online = useOnline()
  const query = useSectorBrokers(sector, period)
  const data = query.data
  // Same rule as the overview: a failed background refetch keeps this
  // sector's brokers on screen rather than swapping them for an error, and
  // `data === null` (`NO_FLOORSHEET_DATA`, queries.ts) is "not summarised
  // yet" rather than a failure.
  const view = resolveViewState(query, { isEmpty: (d) => d === null, online })

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/brokers/sector-flow"
        search={{ period }}
        className="w-fit text-label-m text-muted-foreground hover:text-foreground"
      >
        ← All sectors
      </Link>

      {view === 'loading' ? (
        <Skeleton className="h-96 w-full rounded-lg" />
      ) : view === 'empty' ? (
        <SectorFlowEmpty size="section" />
      ) : view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
          savedAt={query.dataUpdatedAt}
          {...(view === 'failed' ? { title: `Couldn't load ${sector}` } : {})}
        />
      ) : data === undefined || data === null ? null : (
        <SectionCard
          title={sector}
          meta={
            <>
              <span>
                {data.netBuyers} of {data.totalBrokers} net buyers
              </span>
              <WindowNote window={data.window} />
            </>
          }
        >
          <SectorBrokersTable rows={data.brokers} isLoading={false} />
        </SectionCard>
      )}
    </div>
  )
}

/**
 * Brokers · Sector flow.
 *
 * `sector` absent/present names two different hooks, not one hook with a
 * filter (see search.ts) — so it also names two different child components
 * rather than a conditional hook call inside one, which the Rules of Hooks
 * would not allow once `sector` can change without unmounting this screen
 * (clicking a sector row does exactly that, in place).
 *
 * RECOMPOSED AGAINST V2 (`4243:84001`): `ScreenLayout` with the shared rail;
 * the summary as a card with V2's uppercase labels. V2 opens the page with a
 * "TODAY'S READ" banner ("Money moved into hydropower and out of commercial
 * banks. The net amounts are small next to total turnover, so treat this as a
 * lean, not a trend."). Its first line could be templated from `mostIn` /
 * `mostOut`, but the second is a judgement — "small next to total turnover" —
 * with no threshold defined in mm-be or in the spec. Not drawn rather than
 * half-drawn; see the report.
 *
 * Below the sector table, V2's "Broker by sector" grid is its own card on its
 * own endpoint (`BrokerBySectorCard`, `GET /brokers/by-sector/`): the loader
 * prefetches it without awaiting it, and the card draws its own skeleton,
 * failure and "not summarised yet", so it can never blank the page.
 *
 * No period control is drawn on this frame (06-sector-flow.md's own
 * Discrepancy note) despite the endpoint accepting one, so none is built here
 * — and `sectorFlowSearchSchema.period` is PINNED to the literal `'today'`
 * rather than left open, because this screen's card title and table caption
 * both say "today" in words (the table's own column headers — Sector,
 * Bought (Rs), Sold (Rs), Stocks — do not, since the "today" scope is stated
 * once above the table rather than on every money column). See that schema.
 */
function SectorFlowScreen() {
  const { period, sector } = Route.useSearch()

  return (
    <ScreenLayout header={HEADER} pageTitle="Sector Flow">
      {sector === undefined ? (
        <SectorFlowOverview period={period} />
      ) : (
        <SectorFlowDrilldown sector={sector} period={period} />
      )}
    </ScreenLayout>
  )
}
